import { useStoreState } from './easy-peasy';
import { SellerGroupUser } from '../features/seller-group';

export type FeatureFlag =
  | 'simplified_danfe'
  | 'mark_as_collected'
  // Fiscal packaging: `cte_emission` now gates the CT-e + MDF-e package (they go
  // together — MDF-e manifests authorized CT-e keys); `dce_emission` gates DC-e
  // as an independent package. A seller group can have either, both, or neither.
  | 'cte_emission'
  | 'dce_emission'
  // `external_shipping_table` gates the carrier-less External CEP Tables (fiscal
  // CT-e pricing): its own page/menu (/external-tables) and the operation_key ->
  // table binding inside Documentos Fiscais. Independent of `cte_emission` — a
  // group can have CT-e emission without external tables, or vice versa. The
  // server uses the same key (dashboard-api assertFiscalFlag + the external-table
  // BFF), so the two layers light up together. Default false.
  | 'external_shipping_table'
  | 'order_external_id_search'
  | 'supply_order_enabled'
  | 'supply_order_cd_seller_id'
  | 'order_supply_enabled'
  | 'seller_group_notifications_preferences_sms_change'
  | 'enumeration-route-orders'
  | 'expand-orders-per-route'
  | 'export-csv-routes'
  | 'disable-pickup-out-of-covarage'
  | 'enable_payments_page'
  | 'scanner-mode'
  | 'delivery-window'
  | 'enable-analytics-page'
  | 'care_visible'
  | 'care_ai_enabled'
  | 'workflows_visible'
  | 'manage-max-batch-attachments'
  | 'livemode_orders'
  | 'notifications_hub_v2'
  | 'crowdshipping_route_pricing'
  | 'status_aliases'
  // `orders_shared_views` SAIU: as views salvas passaram a morar sempre no
  // dashboard-api, então a tela toda depende de uma flag só (`orders-page-v3`).
  // Ligar/desligar essa chave no `feature_config` não faz mais nada — se ela
  // sobrou em algum seller group, é resíduo e pode ser removida do banco.
  //
  // Pedidos v3: a tela nova INTEIRA (filtros compostos E/OU, views salvas,
  // busca avançada, repaginação verde). Roteia entre `pages/orders.tsx` (v1,
  // byte a byte a da main) e `pages/orders-v3.tsx` — quem escolhe é
  // `pages/orders-entry.tsx`. Default false: sem a flag no seller group, a
  // marca continua na v1.
  | 'orders-page-v3'
  // `dashboard-generativo` libera o Dashboard novo (construtor de blocos) pro
  // seller group, ALÉM do bypass de INTERNAL. Par do `orders-page-v3`: a rota
  // /dashboard, a entrada no sidebar e a do menu vertical abrem quando o usuário
  // é INTERNAL OU a marca tem esta flag em feature_config. Default false.
  | 'dashboard-generativo'
  // `block_manual_action_on_go_leg` desabilita "Baixa manual" e "Atribuir
  // status" quando a entrega atual do pedido é de transportadora GO e ainda não
  // terminou (GO-865). Default false: sem a flag, nada muda. O servidor usa a
  // mesma chave — as duas camadas acendem e apagam juntas.
  | 'block_manual_action_on_go_leg'
  | 'location_health'
  // `driver_onboarding` libera a fila de aprovação de motoristas e a tela de
  // termos (/drivers/onboarding e /drivers/terms) — as duas andam juntas, a
  // fila manda pros termos quando não há versão publicada. Default false.
  | 'driver_onboarding';
// `location_health` (nova) libera o card "Saúde de Localização" na aba
// Localização do editor de filial. Detecta coordenada de cadastro errada
// comparando com o GPS mediano das coletas reais. Default false — só
// seller_groups com a flag explícita em feature_config veem a aba.
// `enable_care` e `care_beta` foram removidas — Care agora usa
// care.seller_group_billing_plan como source of truth do estado do
// chat (vide useCarePlan).
// `care_visible` (nova) controla apenas a VISIBILIDADE do menu Care
// no sidepanel. Ortogonal ao plano: marca pode ter o menu visível
// mas estar em `mode='free'` (e ver paywall no drawer). Default
// effectivo é `false` — só seller_groups com flag explícita true
// veem o menu. Permite rollout staged sem mexer no plano.
// `workflows_visible` segue o mesmo padrão: gate da entrada Workflows
// no sidebar + rota /workflows. Default false; usuários INTERNAL
// veem sempre (bypass interno no helper useCanAccessWorkflows).
// `care_ai_enabled` (nova) libera o modo Care AI pra marca. Ortogonal ao
// `care_visible` e ao billing plan: o Care (Desligado/Manual) é self-serve,
// mas a IA é habilitada MANUALMENTE pela Abbiamo no admin. Default false —
// sem a flag, a marca VÊ o card "Care AI" travado com CTA "fale com seu
// gerente de conta" (ContactSalesDialog) e não consegue ativar. Usuários
// INTERNAL têm bypass. É a flag que o admin liga em sellers_groups.feature_config.

export const getSellerGroupFeatureFlag = (
  flag: FeatureFlag,
  user?: SellerGroupUser | null,
) => {
  if (!user) return false;
  const featureFlags = {
    simplified_danfe: (
      process.env['NX_DANFE_SELLER_GROUP_ID']?.split(',') || []
    )?.includes(user?.seller_group_id || ''),
    ...user?.seller_group?.feature_config,
  };

  return featureFlags[flag];
};

export const useSellerGroupFeatureFlag = (flag: FeatureFlag) => {
  const user = useStoreState((state) => state.login.sellerGroupUser.user);
  if (!user) return false;

  return getSellerGroupFeatureFlag(flag, user);
};

export default useSellerGroupFeatureFlag;
